import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { applyCampaignAttribution } from "../lib/utm";
import { usePlayer } from "../context/PlayerContext";
import { useSettings } from "../context/SettingsContext";
import { releaseSchema, breadcrumbSchema } from "../lib/schema";
import Seo from "../components/Seo";
import DSPButtons from "../components/DSPButtons";
import ShareMenu from "../components/ShareMenu";
import FollowStrip from "../components/FollowStrip";
import EmailCapture from "../components/EmailCapture";
import RelatedReleases from "../components/RelatedReleases";
import Footer from "../components/Footer";
import NotFound from "./NotFound";
import { YouTubeIcon } from "../components/icons/BrandIcons";

function ytId(url) {
  const m = (url || "").match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([\w-]{11})/);
  return m ? m[1] : null;
}

// Social-traffic landing page: /:slug  (e.g. djcodigo.com/unamas). Above-the-fold = artwork/video + one action.
export default function CampaignPage() {
  const { slug } = useParams();
  const { playRelease } = usePlayer();
  const settings = useSettings();
  const [c, setC] = useState(null);
  const [status, setStatus] = useState("loading");
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    api.get(`/campaigns/${slug}`).then((r) => {
      setC(r.data);
      applyCampaignAttribution(r.data);
      track("campaign_view", { releaseId: r.data.releaseId, campaignSlug: r.data.slug });
      setStatus("ok");
    }).catch(() => setStatus("404"));
  }, [slug]);

  if (status === "404") return <NotFound />;
  if (!c) return <div className="min-h-screen bg-ink" />;

  const release = c.release;
  const video = ytId(c.videoUrl || c.youtubeURL);
  const isWatch = c.ctaType === "watch";
  const cta = (action) => track("campaign_cta_click", { releaseId: c.releaseId, campaignSlug: c.slug, metadata: { placement: "campaign_page", action } });

  const primary = () => {
    if (isWatch && video) { cta("watch"); setShowVideo(true); track("video_play", { releaseId: c.releaseId, campaignSlug: c.slug, metadata: { youtubeId: video } }); return; }
    if (release) { cta("listen"); playRelease(release); return; }
    if (c.spotifyURL) { cta("spotify"); window.open(c.spotifyURL, "_blank", "noopener,noreferrer"); }
  };

  const dspRelease = release ? { ...release, spotifyURL: c.spotifyURL || release.spotifyURL, appleMusicURL: c.appleMusicURL || release.appleMusicURL, youtubeURL: c.youtubeURL || release.youtubeURL, youtubeMusicURL: c.youtubeMusicURL || release.youtubeMusicURL }
    : { internalId: null, spotifyURL: c.spotifyURL, appleMusicURL: c.appleMusicURL, youtubeURL: c.youtubeURL, youtubeMusicURL: c.youtubeMusicURL };

  return (
    <div className="grain relative min-h-screen pb-32">
      <Seo
        title={`${c.title} — ${c.headline}`}
        description={c.description || `${c.title} by DJ Código. Listen now on Spotify, Apple Music and YouTube.`}
        image={c.socialImage}
        type="music.song"
        path={`/${c.slug}`}
        jsonLd={[release ? releaseSchema(release, settings) : null, breadcrumbSchema([{ name: "Home", path: "/" }, { name: c.title, path: `/${c.slug}` }], settings)]}
      />
      <div className="mx-auto max-w-lg px-5 pt-8 sm:max-w-5xl sm:px-10 sm:pt-20">
        <div className="grid items-start gap-6 sm:grid-cols-2 sm:gap-14">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="relative">
            <div className="absolute -inset-4 rounded-full bg-gold/15 blur-3xl" />
            {showVideo && video ? (
              <div className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black">
                <iframe data-testid="campaign-video" title={c.title} src={`https://www.youtube-nocookie.com/embed/${video}?autoplay=1&rel=0`} className="h-full w-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <div className="relative aspect-square overflow-hidden rounded-2xl border border-white/10">
                <img src={c.artwork} alt={`${c.title} — DJ Código cover art`} className="h-full w-full object-cover" fetchPriority="high" />
                <button data-testid="campaign-art-play" onClick={primary} aria-label={c.ctaLabel} className="absolute bottom-4 right-4 flex h-14 w-14 items-center justify-center rounded-full bg-gold text-black shadow-xl transition-transform active:scale-90">
                  {isWatch ? <YouTubeIcon size={20} /> : <Play size={22} fill="currentColor" className="ml-0.5" />}
                </button>
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}>
            <p className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.25em] text-gold">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gold" /> {c.headline}
            </p>
            <h1 data-testid="campaign-title" className="mt-4 font-display text-4xl font-bold uppercase leading-none tracking-tight text-white sm:text-6xl">{c.title}</h1>
            <p className="mt-2 text-lg text-gold">DJ Código</p>
            {c.description && <p data-testid="campaign-description" className="mt-4 text-base leading-relaxed text-white/70">{c.description}</p>}

            <div className="mt-7 flex flex-col gap-3">
              <button data-testid="campaign-primary-cta" onClick={primary} className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-gold px-8 text-sm font-bold uppercase tracking-wide text-black shadow-lg shadow-gold/20 transition-transform active:scale-95 hover:bg-gold-2">
                {isWatch ? <YouTubeIcon size={18} /> : <Play size={18} fill="currentColor" />} {c.ctaLabel}
              </button>
              {!isWatch && video && (
                <button data-testid="campaign-watch-cta" onClick={() => { cta("watch"); setShowVideo(true); track("video_play", { releaseId: c.releaseId, campaignSlug: c.slug, metadata: { youtubeId: video } }); }} className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 text-xs font-semibold uppercase tracking-wide text-white/90 transition-transform active:scale-95">
                  <YouTubeIcon size={16} /> Watch Video
                </button>
              )}
              {isWatch && release && (
                <button data-testid="campaign-listen-cta" onClick={() => { cta("listen"); playRelease(release); }} className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 text-xs font-semibold uppercase tracking-wide text-white/90 transition-transform active:scale-95">
                  <Play size={15} fill="currentColor" /> Stream the Song
                </button>
              )}
            </div>

            <div className="mt-5"><DSPButtons release={dspRelease} /></div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <ShareMenu kind="c" slug={c.slug} title={c.title} text={`${c.title} — DJ Código`} releaseId={c.releaseId} eventType="share_song" label="Share This Track" />
              {release && (
                <Link to={`/music/${release.slug}`} data-testid="campaign-release-link" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-white/50 transition-colors hover:text-gold">
                  Song page <ArrowRight size={13} />
                </Link>
              )}
            </div>
            <FollowStrip settings={settings} placement="campaign" className="mt-8" />
          </motion.div>
        </div>
      </div>

      <RelatedReleases current={release} overline="More from DJ Código" title="Keep the Night Going" />
      <EmailCapture source={`campaign:${c.slug}`} releaseId={c.releaseId} />
      <Footer settings={settings} />
    </div>
  );
}
